import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  Link,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { dynamicallyCompare } from '../../../abelcommon';
import { cardList } from './miner-card-data';
import { useTranslation } from 'react-i18next';

type CardListKey = keyof (typeof cardList)[0] | 'revenue' | 'revenueUSD';

interface MinerFilterRevenueSectionProps {
  cardWithHashrate: Array<{
    id: number;
    cuda_type: string;
    hash_rate: number;
    revenue: string;
    revenueUSD: string;
  }>;
  searchValue: string;
  onSelectId: (id: number) => void;
}

export const MinerFilterRevenueSection = ({
  cardWithHashrate,
  onSelectId,
  searchValue,
}: MinerFilterRevenueSectionProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<{ column: CardListKey; direction: boolean }>({
    column: 'cuda_type',
    direction: true,
  });
  const { t } = useTranslation();

  const tableHeader: Array<{ label: string; key: CardListKey }> = [
    { label: t('minerRevenueSectionPage.cardType'), key: 'cuda_type' },
    { label: `${t('minerRevenueSectionPage.hashRate')} (Mh/s)`, key: 'hash_rate' },
    { label: `ABEL ${t('minerRevenueSectionPage.ABELRevenue')} (24h)`, key: 'revenue' },
    { label: `USD ${t('minerRevenueSectionPage.ABELRevenue')} (24h)`, key: 'revenueUSD' },
  ];
  const filteredCard = useMemo(() => {
    return cardWithHashrate.filter(({ cuda_type }) => cuda_type.toLocaleLowerCase().includes(searchValue));
  }, [cardWithHashrate, searchValue]);

  const paginatedCardList = useMemo(() => {
    return filteredCard
      .sort((a, b) =>
        orderBy.direction
          ? dynamicallyCompare(a[orderBy.column], b[orderBy.column])
          : dynamicallyCompare(b[orderBy.column], a[orderBy.column])
      )
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredCard, page, rowsPerPage, orderBy.direction, orderBy.column]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);
  return (
    <Card>
      <CardContent sx={{ overflow: 'auto' }}>
        {filteredCard.length > 0 ? (
          <>
            <TableContainer sx={{ overflow: 'auto' }}>
              <Table aria-label="simple table" sx={{ minWidth: '500px' }}>
                <TableHead>
                  <TableRow>
                    {tableHeader.map(({ label, key }) => (
                      <TableCell key={key}>
                        <TableSortLabel
                          active={orderBy.column === key}
                          direction={orderBy.column === key ? (orderBy.direction ? 'asc' : 'desc') : 'asc'}
                          onClick={() => setOrderBy((prevState) => ({ column: key, direction: !prevState.direction }))}
                        >
                          {label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCardList.map((row, index) => (
                    <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell component="th" scope="row">
                        <Link
                          onClick={() => onSelectId(row.id)}
                          sx={{ color: 'appBlue.contrastText', cursor: 'pointer' }}
                        >
                          {row.cuda_type}
                        </Link>
                      </TableCell>
                      <TableCell>{row.hash_rate}</TableCell>
                      <TableCell>{row.revenue} ABEL</TableCell>
                      <TableCell>≈ {row.revenueUSD} USD</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              sx={{ pl: 0 }}
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredCard.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, page) => setPage(page)}
              labelRowsPerPage={t('global.rowsPerPage')}
              onRowsPerPageChange={(e) => setRowsPerPage(+e.target.value)}
            />
          </>
        ) : (
          <>
            <Typography variant="body1" align="center" sx={{ color: 'appBlue.contrastText', fontSize: '2rem' }}>
              {t('global.noData')}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};
